import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-[#2d5f3f] text-[#2d5f3f] hover:bg-[#2d5f3f] hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <img
              src="https://static.wixstatic.com/media/5924bc_1d4a840f9cb44afcb7ee8f647411679d~mv2.png/v1/fill/w_212,h_93,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/5924bc_1d4a840f9cb44afcb7ee8f647411679d~mv2.png"
              alt="Equi-Go Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 font-['Playfair_Display']">
            Privacy Policy
          </h1>
          <p className="text-gray-600 mb-8">Last updated: January 2025</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                Equi-Go ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website or use our horsebox hire services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.1 Personal Information</h3>
              <p className="mb-3">When you make a booking enquiry or hire our horsebox, we may collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Preferred hire dates</li>
                <li>Driving license details (photo copy)</li>
                <li>Proof of address documents</li>
                <li>Payment information (processed securely via bank transfer)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.2 Automatically Collected Information</h3>
              <p className="mb-3">When you visit our website, we may automatically collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="mb-3">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Process your horsebox hire booking and enquiries</li>
                <li>Communicate with you about your booking</li>
                <li>Verify your identity and driving license</li>
                <li>Process payments and manage deposits</li>
                <li>Send booking confirmations and reminders</li>
                <li>Improve our services and website functionality</li>
                <li>Comply with legal obligations (insurance requirements, DEFRA regulations)</li>
                <li>Respond to your questions and provide customer support</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Basis for Processing (GDPR)</h2>
              <p className="mb-3">We process your personal data based on:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contract Performance:</strong> To fulfill our horsebox hire agreement with you</li>
                <li><strong>Legal Obligation:</strong> To comply with insurance and transport regulations</li>
                <li><strong>Legitimate Interest:</strong> To operate our business and improve our services</li>
                <li><strong>Consent:</strong> For marketing communications (you can opt out at any time)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
              <p className="mb-3">We do not sell your personal information. We may share your data with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Insurance Providers:</strong> To verify coverage for drivers</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                <li><strong>Service Providers:</strong> Email service providers, website hosting (with appropriate safeguards)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. Your documents (driving license, proof of address) 
                are stored securely and accessed only by authorized personnel.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="mb-3">We retain your personal information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Booking information: For 7 years (tax and legal requirements)</li>
                <li>Driving license and ID documents: For 7 years (insurance requirements)</li>
                <li>Email communications: For 7 years (business records)</li>
                <li>Website analytics: For 26 months</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights (GDPR)</h2>
              <p className="mb-3">Under the GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your data (subject to legal obligations)</li>
                <li><strong>Restriction:</strong> Limit how we use your data</li>
                <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> For marketing communications</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, contact us at <a href="mailto:enquiries@equi-go.co.uk" className="text-[#2d5f3f] hover:underline">enquiries@equi-go.co.uk</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies</h2>
              <p>
                Our website uses cookies to improve your experience. For detailed information about the cookies we use, 
                please see our <a href="/cookie-policy" className="text-[#2d5f3f] hover:underline">Cookie Policy</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites (e.g., Facebook, Google Maps). We are not responsible 
                for the privacy practices of these websites. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal 
                information from children. Drivers must be 25-75 years old as per our insurance policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated 
                "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="mb-3">If you have questions about this Privacy Policy or wish to exercise your rights, contact us:</p>
              <div className="bg-gray-50 p-6 rounded-xl">
                <p className="font-semibold text-gray-900">Equi-Go Horsebox Hire</p>
                <p>North Yorkshire, between Harrogate and Boroughbridge</p>
                <p className="mt-2">
                  Email: <a href="mailto:enquiries@equi-go.co.uk" className="text-[#2d5f3f] hover:underline">enquiries@equi-go.co.uk</a>
                </p>
                <p>
                  Phone: <a href="tel:07788754165" className="text-[#2d5f3f] hover:underline">07788 754165</a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Complaints</h2>
              <p>
                If you believe your data protection rights have been violated, you have the right to lodge a complaint with 
                the UK Information Commissioner's Office (ICO):
              </p>
              <div className="bg-gray-50 p-6 rounded-xl mt-3">
                <p className="font-semibold text-gray-900">Information Commissioner's Office</p>
                <p>Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</p>
                <p className="mt-2">
                  Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#2d5f3f] hover:underline">ico.org.uk</a>
                </p>
                <p>Helpline: 0303 123 1113</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;